import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { MagnifyingGlass } from "@phosphor-icons/react";

const PLAN_BADGE = {
    free: "bg-gray-100 text-gray-700",
    standard: "bg-blue-100 text-blue-800",
    pro: "bg-purple-100 text-purple-800",
    enterprise: "bg-emerald-100 text-emerald-800",
};
const STATUS_BADGE = {
    active: "bg-emerald-100 text-emerald-800",
    suspended: "bg-amber-100 text-amber-800",
    cancelled: "bg-rose-100 text-rose-800",
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

export default function SubscribersPane({ meOrgId }) {
    const [q, setQ] = useState("");
    const [planFilter, setPlanFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("limit", "200");
            if (q.trim()) params.set("q", q.trim());
            if (planFilter) params.set("plan", planFilter);
            if (statusFilter) params.set("status", statusFilter);
            const { data } = await api.get(`/admin/panel/subscribers?${params.toString()}`);
            setRows(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [q, planFilter, statusFilter]);

    useEffect(() => { load(); }, [planFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // keep the open detail panel in sync with fresh data
    useEffect(() => {
        if (detail) {
            setDetail((d) => rows.find((r) => r.user_id === d?.user_id) || null);
        }
    }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

    const patchOrg = async (row, body, okMsg) => {
        try {
            await api.patch(`/admin/panel/organizations/${row.organization_id}`, body);
            toast.success(okMsg || "Aggiornato");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-subscribers-pane" className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex-1 relative max-w-md min-w-[220px]">
                    <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        data-testid="subscribers-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Cerca per nome o email…"
                        className="h-10 w-full pl-9 pr-3 border border-gray-300 text-sm focus:border-black focus:outline-none"
                    />
                </div>
                <select data-testid="subscribers-plan-filter" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
                        className="h-10 px-2 border border-gray-300 text-xs uppercase tracking-wider bg-white">
                    <option value="">Tutti i piani</option>
                    <option value="free">Free</option>
                    <option value="standard">Standard</option>
                    <option value="pro">Pro</option>
                </select>
                <select data-testid="subscribers-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        className="h-10 px-2 border border-gray-300 text-xs uppercase tracking-wider bg-white">
                    <option value="">Tutti gli stati</option>
                    <option value="active">Attivo</option>
                    <option value="suspended">Sospeso</option>
                    <option value="cancelled">Cancellato</option>
                </select>
                <button onClick={load} data-testid="subscribers-search-btn"
                        className="h-10 px-4 bg-black text-white text-xs font-semibold hover:bg-gray-800">Cerca</button>
            </div>

            <div className="border border-gray-200 bg-white overflow-x-auto" data-testid="subscribers-table">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500 animate-pulse">Caricamento…</div>
                ) : rows.length === 0 ? (
                    <div className="p-6 text-sm text-gray-500">Nessun utente trovato.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                                <th className="px-3 py-2 font-semibold">Utente</th>
                                <th className="px-3 py-2 font-semibold">Registrato</th>
                                <th className="px-3 py-2 font-semibold">Piano</th>
                                <th className="px-3 py-2 font-semibold">Stato abb.</th>
                                <th className="px-3 py-2 font-semibold">Rinnovo</th>
                                <th className="px-3 py-2 font-semibold">Disponibili</th>
                                <th className="px-3 py-2 font-semibold">Usate</th>
                                <th className="px-3 py-2 font-semibold">Tot. analisi</th>
                                <th className="px-3 py-2 font-semibold text-right">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.user_id} data-testid={`subscriber-row-${r.user_id}`}
                                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                                    <td className="px-3 py-2">
                                        <div className="font-semibold flex items-center gap-1.5">
                                            {[r.first_name, r.last_name].filter(Boolean).join(" ") || r.full_name}
                                            {r.plan !== "enterprise" && (
                                                <span data-testid={`subscriber-type-${r.user_id}`}
                                                      className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-bold ${r.organization_type === "company" ? "bg-indigo-100 text-indigo-800" : "bg-gray-100 text-gray-600"}`}>
                                                    {r.organization_type === "company" ? "Azienda" : "Privato"}
                                                </span>
                                            )}
                                        </div>
                                        <div className="font-mono-tight text-[11px] text-gray-500">{r.email}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{fmtDate(r.registered_at)}</td>
                                    <td className="px-3 py-2">
                                        <select value={r.plan}
                                                data-testid={`subscriber-plan-${r.user_id}`}
                                                onChange={(e) => patchOrg(r, { plan: e.target.value }, "Piano aggiornato")}
                                                className={`text-[10px] uppercase tracking-wider px-2 py-1 border border-transparent ${PLAN_BADGE[r.plan] || PLAN_BADGE.free} cursor-pointer hover:border-gray-400`}>
                                            {["free", "standard", "pro", "enterprise"].map((p) => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span data-testid={`subscriber-status-${r.user_id}`}
                                              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${STATUS_BADGE[r.subscription_status] || STATUS_BADGE.active}`}>
                                            {r.subscription_status}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{r.next_renewal ? r.next_renewal.split("-").reverse().join("/") : (r.one_time ? "una tantum" : "—")}</td>
                                    <td className="px-3 py-2 font-mono-tight">{r.unlimited ? "∞" : r.remaining}</td>
                                    <td className="px-3 py-2 font-mono-tight">{r.unlimited ? r.used : `${r.used}/${r.limit}`}</td>
                                    <td className="px-3 py-2 font-mono-tight">{r.analyses_total}</td>
                                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                                        <button
                                            data-testid={`subscriber-credits-${r.user_id}`}
                                            onClick={() => {
                                                const v = window.prompt(`Nuovo limite analisi per ${r.email} (attuale: ${r.limit})`, r.limit);
                                                if (v !== null && !Number.isNaN(Number(v))) {
                                                    patchOrg(r, { monthly_analysis_limit: Number(v) }, "Limite analisi aggiornato");
                                                }
                                            }}
                                            className="text-xs px-2 py-1 border border-gray-300 hover:border-black">
                                            Crediti
                                        </button>
                                        <button
                                            data-testid={`subscriber-suspend-${r.user_id}`}
                                            disabled={r.organization_id === meOrgId}
                                            onClick={() => patchOrg(r, { subscription_status: r.subscription_status === "suspended" ? "active" : "suspended" },
                                                r.subscription_status === "suspended" ? "Account riattivato" : "Account sospeso")}
                                            className={`text-xs px-2 py-1 border disabled:opacity-40 ${r.subscription_status === "suspended" ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-emerald-700" : "border-amber-300 bg-amber-50 text-amber-800 hover:border-amber-700"}`}>
                                            {r.subscription_status === "suspended" ? "Riattiva" : "Sospendi"}
                                        </button>
                                        <button data-testid={`subscriber-detail-${r.user_id}`}
                                                onClick={() => setDetail(detail?.user_id === r.user_id ? null : r)}
                                                className="text-xs px-2 py-1 border border-gray-300 hover:border-black">
                                            Dettaglio
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {detail && (
                <div className="border border-gray-300 bg-gray-50 p-4 text-xs space-y-1" data-testid="subscriber-detail-panel">
                    <div className="overline mb-2">Dettaglio utente</div>
                    <div><b>Nome:</b> {[detail.first_name, detail.last_name].filter(Boolean).join(" ") || detail.full_name} · <b>Tipo:</b> {detail.organization_type === "company" ? "Azienda" : "Privato"}{detail.phone ? <> · <b>Cellulare:</b> {detail.phone}</> : null}</div>
                    <div><b>Email:</b> {detail.email} · <b>Ruolo:</b> {detail.role} · <b>Organizzazione:</b> {detail.organization_name}</div>
                    <div><b>Registrato il:</b> {fmtDate(detail.registered_at)} · <b>Inizio abbonamento:</b> {fmtDate(detail.subscription_started_at)}</div>
                    <div><b>Piano:</b> {detail.plan_label} ({detail.price_eur > 0 ? `€${detail.price_eur.toFixed(2).replace(".", ",")}/mese` : "gratuito"}) · <b>Stato:</b> {detail.subscription_status}</div>
                    <div><b>Analisi incluse:</b> {detail.unlimited ? "illimitate" : detail.limit}{detail.one_time ? " (una tantum)" : "/mese"} · <b>Usate:</b> {detail.used} · <b>Disponibili:</b> {detail.unlimited ? "∞" : detail.remaining} · <b>Totale effettuate:</b> {detail.analyses_total}</div>
                    {detail.organization_type === "company" && detail.billing && (
                        <div className="border-t border-gray-200 pt-2 mt-2" data-testid="subscriber-billing-detail">
                            <div className="overline mb-1">Dati di fatturazione</div>
                            <div><b>Ragione sociale:</b> {detail.billing.company_name} · <b>P.IVA:</b> {detail.billing.vat_number}{detail.billing.fiscal_code ? <> · <b>CF:</b> {detail.billing.fiscal_code}</> : null}</div>
                            <div><b>Sede legale:</b> {detail.billing.address}, {detail.billing.zip_code} {detail.billing.city} ({detail.billing.province}), {detail.billing.country}</div>
                            <div>{detail.billing.sdi_code ? <><b>Codice SDI:</b> {detail.billing.sdi_code}</> : null}{detail.billing.sdi_code && detail.billing.pec ? " · " : ""}{detail.billing.pec ? <><b>PEC:</b> {detail.billing.pec}</> : null}</div>
                        </div>
                    )}
                </div>
            )}
            <div className="text-xs text-gray-500">{rows.length} utenti</div>
        </div>
    );
}
